import { TYPESENSE_SELECT_OPTIONS_WINDOW_SIZE } from '../constants';
import {
  GetSelectOptionByIdFunction,
  SearchOptions,
  SelectOptionSearchFunction,
  TypesenseCollection,
} from '../types';
import { searchCollection, TypesenseDocument } from '../utils';

const BASE_FILTER = 'IsSupplier:true';
const BASE_FILTER_ACTIVE = 'Active:true && IsSupplier:true';

export const getSearchSupplierSelectOptions: SelectOptionSearchFunction =
  async (
    searchValue: string,
    apiKey: string,
    pageIndex: number = 1,
    options: SearchOptions = {},
  ) => {
    const results = await searchCollection(
      TypesenseCollection.SUPPLIERS_VIEW.name,
      'Name',
      searchValue,
      apiKey,
      pageIndex,
      TYPESENSE_SELECT_OPTIONS_WINDOW_SIZE,
      options.filterBy
        ? `${options.filterBy} && ${BASE_FILTER_ACTIVE}`
        : BASE_FILTER_ACTIVE,
      options.sortBy ? `${options.sortBy}` : 'Name:asc',
    );
    if (!results.success || results.error) {
      throw new Error(
        results.error || 'Failed to fetch suppliers from Typesense',
      );
    }

    const mappedResults = results?.documents?.map(
      (item: TypesenseDocument) => ({
        value: item.Name,
        id: item.SourceId.toString(),
      }),
    );
    return mappedResults ?? [];
  };

/**
 * Retrieves a supplier select option by ID from Typesense.
 * @throws {Error} When the search fails, returns an error, or no supplier is found with the given ID
 */
export const getSupplierSelectOptionById: GetSelectOptionByIdFunction = async (
  id: string,
  apiKey: string,
  additionalFilterOptions?: SearchOptions['filterBy'],
) => {
  const results = await searchCollection(
    TypesenseCollection.SUPPLIERS_VIEW.name,
    'Name',
    '*',
    apiKey,
    1,
    1,
    additionalFilterOptions
      ? `${additionalFilterOptions} && ${BASE_FILTER} && SourceId:=${id}`
      : `${BASE_FILTER} && SourceId:=${id}`,
  );

  if (!results.success || results.error) {
    throw new Error(
      results.error || 'Failed to fetch supplier by id from Typesense',
    );
  }

  const mappedResults = results?.documents?.map((item: TypesenseDocument) => ({
    value: item.Name,
    id: item.SourceId.toString(),
  }));

  if (!mappedResults || mappedResults.length === 0) {
    //  debug only here as we just throw an error to be able to use try-catch, but it is not necessarily an actual error
    console.debug(`Supplier with id ${id} not found`);
    throw new Error(`Supplier with id ${id} not found`);
  }

  return mappedResults[0];
};
